import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { CompanyProductTeam } from 'src/app/models/company_product_team.model';
import { CompanyProductUserMember } from 'src/app/models/company_product_user_member.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { CompanyProductTeamCreditDialogComponent } from './company-product-team-credit-dialog/company-product-team-credit-dialog.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CompanyProductPageIdentityComponent } from '../company-product-page-identity/company-product-page-identity.component';
import { UserListDialogComponent } from 'src/app/components/main/shared/user-list-dialog/user-list-dialog.component';
import { UserListDialogData } from 'src/app/models/user-list-dialog.model';
import { buildCompanyProductMemberRoleBadges, indexCompanyProductMembersByUserId } from 'src/app/utils/company-product-member-role-badges';

@Component({
  selector: 'app-company-product-teams',
  templateUrl: './company-product-teams.component.html',
  styleUrls: ['./company-product-teams.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    CompanyProductPageIdentityComponent,
  ],
})
export class CompanyProductTeamsComponent implements OnInit {
  protected activatedRoute = inject(ActivatedRoute);
  private api = inject(ApiService);
  private permissions = inject(PermissionsService);
  private dialog = inject(MatDialog);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  displayed_columns: string[] = ['name', 'users', 'phases', 'actions'];
  data_source: MatTableDataSource<CompanyProductTeam> = new MatTableDataSource();
  can_manage_teams = signal(false);

  private company_product_id: string = null as any;
  private product_members_by_user_id = new Map<number, CompanyProductUserMember>();

  ngOnInit(): void {
    this.initCompanyProductIdParam();
    this.can_manage_teams.set(this.permissions.isAdmin() || this.permissions.isAccountManagerInCompanyProduct(this.company_product_id));
    this.fetchTeams();
  }

  add() {
    this.openCreditDialog();
  }

  edit(pt: CompanyProductTeam) {
    this.openCreditDialog(pt);
  }

  delete(pt: CompanyProductTeam) {
    this.api.deleteCompanyProductTeam(pt.id.toString()).subscribe(() => {
      this.fetchTeams();
    });
  }

  phasesLabel(team: CompanyProductTeam): string {
    const phases = team.company_product_phases ?? [];
    if (phases.length === 0) {
      return 'Sin fases';
    }

    return phases.map(phase => phase.company_product_phase_type?.name ?? `Fase #${phase.id}`).join(', ');
  }

  usersLabel(team: CompanyProductTeam): string {
    const count = team.users?.length ?? 0;
    return `${count} ${count === 1 ? 'usuario' : 'usuarios'}`;
  }

  openTeamMembersDialog(team: CompanyProductTeam): void {
    const dialog_data: UserListDialogData = {
      title: team.name,
      subtitle: `${team.users.length} ${team.users.length === 1 ? 'miembro' : 'miembros'}`,
      users: team.users.map(user => ({
        id: user.id,
        full_name: user.full_name || `${user.name} ${user.surnames}`.trim(),
        pokemon_profile_picture_url: user.pokemon_profile_picture_url ?? null,
        serious_profile_picture_url: user.serious_profile_picture_url ?? null,
        badges: buildCompanyProductMemberRoleBadges(this.product_members_by_user_id.get(user.id)),
      })),
    };

    this.dialog.open(UserListDialogComponent, {
      data: dialog_data,
      width: '520px',
      maxWidth: '92vw',
      autoFocus: false,
      panelClass: 'user-list-dialog',
    });
  }

  private initCompanyProductIdParam() {
    this.company_product_id = this.activatedRoute.snapshot.parent?.parent?.paramMap.get('id') as string;
  }

  private fetchTeams() {
    forkJoin({
      teams: this.api.getCompanyProductCompanyProductTeams(this.company_product_id, {
        with: 'users,company_product_phases..company_product_phase_type',
      }),
      members: this.api.getBisualProductionTeam(this.company_product_id),
    }).subscribe(({ teams, members }) => {
      this.data_source.data = teams;
      this.product_members_by_user_id = indexCompanyProductMembersByUserId(members);
    });
  }

  private openCreditDialog(pt: CompanyProductTeam | undefined = undefined) {
    const dialogRef = this.dialog.open(CompanyProductTeamCreditDialogComponent, {
      width: '600px',
      data: {
        company_product_team: pt ?? { company_product_id: this.company_product_id },
      },
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data != null) {
        this.fetchTeams();
      }
    });
  }
}
